import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ShieldOff, Shield, Lock, ChevronRight } from 'lucide-react';
import { roleService } from '../../services/roleService';
import { systemService } from '../../services/systemService';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../lib/permissions';
import { PermissionLine } from './PermissionPicker';
import RoleEditor from './RoleEditor';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';

const RolesContent = () => {
    const { hasPermission } = useAuth();
    const { catalog, error: catalogError } = useCatalog();
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(null);

    const fetchRoles = useCallback(async () => {
        try {
            setError('');
            const [rolesRes, usersRes] = await Promise.all([
                roleService.getAll(),
                hasPermission('USERS:READ') ? systemService.getAll().catch(() => null) : null,
            ]);
            setRoles(rolesRes?.data || []);
            setUsers(usersRes?.data || []);
        } catch (err) {
            setError(err.message || 'Failed to load roles');
        } finally {
            setLoading(false);
        }
    }, [hasPermission]);

    useEffect(() => { fetchRoles(); }, [fetchRoles]);

    if (!hasPermission('ROLES:READ')) {
        return (
            <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                <ShieldOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600">You do not have permission to view roles.</p>
            </div>
        );
    }

    if (loading || (!catalog && !catalogError)) return <LoadingSpinner message="Loading roles..." />;

    if (open) {
        return (
            <RoleEditor
                catalog={catalog}
                role={open === 'new' ? null : open}
                roles={roles}
                users={users}
                onBack={() => setOpen(null)}
                onSaved={async () => { await fetchRoles(); setOpen(null); }}
            />
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Roles & Permissions</h2>
                    <p className="text-gray-600 mt-1">What each role can see and do. Give people a role in Users.</p>
                </div>
                {hasPermission('ROLES:CREATE') && (
                    <button
                        onClick={() => setOpen('new')}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        New role
                    </button>
                )}
            </div>

            {(error || catalogError) && <ErrorAlert error={error || catalogError} onRetry={fetchRoles} />}

            {catalog && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">People</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {roles.map((role) => {
                                const holders = users.filter((u) => u.roleName === role.name);
                                return (
                                    <tr key={role.id} onClick={() => setOpen(role)} className="cursor-pointer hover:bg-gray-50 transition">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-600">
                                                    <Shield className="w-4 h-4" />
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-900">{role.label || role.name}</span>
                                                        {!role.editable && (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                                                <Lock className="w-3 h-3" />Fixed
                                                            </span>
                                                        )}
                                                        {!role.builtIn && (
                                                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Custom</span>
                                                        )}
                                                    </div>
                                                    {role.description && <p className="truncate text-sm text-gray-500">{role.description}</p>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <PermissionLine catalog={catalog} permissions={role.permissionNames} everything={!role.editable} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm" title={holders.map((u) => `${u.firstName} ${u.lastName}`).join(', ')}>
                                            {role.members > 0
                                                ? <span className="text-gray-700"><span className="font-semibold">{role.members}</span> {role.members === 1 ? 'person' : 'people'}</span>
                                                : <span className="text-gray-400">Nobody yet</span>}
                                        </td>
                                        <td className="pr-4 text-gray-400"><ChevronRight className="w-4 h-4" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RolesContent;
